<script setup lang="ts">
import { getTranslatedProperty } from "@shopware/helpers";
import { computed, provide, reactive, ref } from "vue";
import type { ComputedRef, UnwrapNestedRefs } from "vue";
import { defu } from "defu";
import SwProductListingFilter from "./SwProductListingFilter.vue";
import type {
  CmsElementProductListing,
  CmsElementSidebarFilter,
} from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { useCategory, useListing } from "#imports";
import { onClickOutside } from "@vueuse/core";
import { useRoute, useRouter } from "vue-router";
import type { RequestParameters } from "#shopware";
import { getCategoryBreadcrumbs } from "@shopware/helpers";
import SwProductListingSelectedFilter from "~/components/cms/SwProductListingSelectedFilter.vue";

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
  listingType?: string;
}>();

type Translations = {
  listing: {
    sort: string;
    resetFilters: string;
  };
};

let translations: Translations = {
  listing: {
    sort: "Sort",
    resetFilters: "Reset filters",
  },
};

translations = defu(translations, useCmsTranslations()) as Translations;

const { category } = useCategory();
const route = useRoute();
const router = useRouter();
const sortingLabel = ref('Topseller')

const isSortMenuOpen = ref(false);
const {
  getCurrentSortingOrder,
  changeCurrentSortingOrder,
  getSortingOrders,
  getInitialFilters,
  search,
  getCurrentFilters,
  filtersToQuery,
} = useListing({ listingType: "categoryListing" });

const sidebarSelectedFilters: UnwrapNestedRefs<{
  [key: string]: any;
}> = reactive<{
  manufacturer: Set<any>;
  properties: Set<any>;
  "min-price": string | number | undefined;
  "max-price": string | number | undefined;
  rating: string | number | undefined;
  "shipping-free": boolean | undefined;
}>({
  manufacturer: new Set(),
  properties: new Set(),
  "min-price": undefined,
  "max-price": undefined,
  rating: undefined,
  "shipping-free": undefined,
});

const searchCriteriaForRequest: ComputedRef<RequestParameters<"searchPage">> =
  computed(() => ({
    // turn Set to array and then into string with | separator
    manufacturer: [...sidebarSelectedFilters.manufacturer]?.join("|"),
    // turn Set to array and then into string with | separator
    properties: [...sidebarSelectedFilters.properties]?.join("|"),
    "min-price": sidebarSelectedFilters["min-price"],
    "max-price": sidebarSelectedFilters["max-price"],
    order: getCurrentSortingOrder.value,
    "shipping-free": sidebarSelectedFilters["shipping-free"],
    rating: sidebarSelectedFilters["rating"],
    search: "",
    limit:24
  }));

for (const param in route.query) {
  if (sidebarSelectedFilters.hasOwnProperty(param)) {
    if (
      sidebarSelectedFilters[param] &&
      typeof sidebarSelectedFilters[param] === "object"
    ) {
      (route.query[param] as unknown as string)
        .split("|")
        .forEach((element) => {
          sidebarSelectedFilters[param].add(element);
        });
    } else {
      sidebarSelectedFilters[param] = route.query[param];
    }
  }
}

const onOptionSelectToggle = async ({
  code,
  value,
}: {
  code: string;
  value: any;
}) => {
  if (!["properties", "manufacturer"]?.includes(code)) {
    sidebarSelectedFilters[code] = value;
  } else {
    if (sidebarSelectedFilters[code]?.has(value)) {
      sidebarSelectedFilters[code]?.delete(value);
    } else {
      sidebarSelectedFilters[code]?.add(value);
    }
  }

  await search(searchCriteriaForRequest.value);
  await router.push({
    query: {
      ...filtersToQuery(searchCriteriaForRequest.value),
    },
  });
};

const clearFilters = async () => {
  sidebarSelectedFilters.manufacturer.clear();
  sidebarSelectedFilters.properties.clear();
  sidebarSelectedFilters["min-price"] = undefined;
  sidebarSelectedFilters["max-price"] = undefined;
  sidebarSelectedFilters["rating"] = undefined;
  sidebarSelectedFilters["shipping-free"] = undefined;
  search(searchCriteriaForRequest.value);
  await router.push({
    query: {
      ...filtersToQuery(searchCriteriaForRequest.value),
    },
  });
};


const activeSorting = ref('Sortieren');

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    setActiveSorting(order);

    await changeCurrentSortingOrder(
      order,
      route.query as unknown as RequestParameters<"searchPage">,
    );
  },
});

const selectedOptionIds = computed(() => [
  ...sidebarSelectedFilters.properties,
  ...sidebarSelectedFilters.manufacturer,
]);
provide("selectedOptionIds", selectedOptionIds);

async function invokeCleanFilters() {
  await search({ search: "", order: currentSortingOrder.value });
  await clearFilters();
}

const dropdownElement = ref(null);
onClickOutside(dropdownElement, () => (isSortMenuOpen.value = false));

if (category.value) {
  const breadcrumbs = getCategoryBreadcrumbs(category.value, {
    startIndex: 1,
  });
  useBreadcrumbs(breadcrumbs);
}

const setActiveSorting = (order = 'topseller')=> {
  const matchedOrder = getSortingOrders?.value?.find(item => item.key === order);
  if(matchedOrder) {
    activeSorting.value = matchedOrder.label
  }
}

onMounted(async ()=> {
  await setActiveSorting('topseller');
})

</script>
<template>
  <LayoutBreadcrumbs />
  <div class="form form--filters product-filter">
    <div class="row" v-if="getInitialFilters.length">
      <div v-for="filter in getInitialFilters"
           :key="`${filter?.id || filter?.code}`"
           class="col-md-4 mb-md-4 mt-0">
        <SwProductListingFilter
            @selectFilterValue="onOptionSelectToggle"
            :selectedFilters="getCurrentFilters"
            :filter="filter"
            class="relative"
        />
      </div>

      <div class="mx-auto mt-4 mb-2 section__selected-filters js-selected-filters-container">
        <ul>
          <SwProductListingSelectedFilter
              v-for="filter in getInitialFilters"
              :key="`${filter?.id || filter?.code}`"
              @selectFilterValue="onOptionSelectToggle"
              :selectedFilters="getCurrentFilters"
              :filter="filter"
          />
        </ul>
        <div class="section__selected-filters-actions">
          <a class="reset-filter" href="javascript:;" @click="invokeCleanFilters" v-if="getCurrentFilters.properties != ''">
            {{ $t('listing.resetFilters') }}
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.146021 1.3539C0.0995332 1.30741 0.0626571 1.25222 0.037498 1.19148C0.0123389 1.13074 -0.000610351 1.06564 -0.000610352 0.999899C-0.000610352 0.934155 0.0123389 0.869055 0.037498 0.808316C0.0626571 0.747576 0.0995332 0.692387 0.146021 0.645899C0.192509 0.599411 0.247698 0.562535 0.308438 0.537376C0.369177 0.512217 0.434277 0.499268 0.500021 0.499268C0.565765 0.499268 0.630865 0.512217 0.691605 0.537376C0.752344 0.562535 0.807533 0.599411 0.854021 0.645899L6.00002 5.7929L11.146 0.645899C11.1925 0.599411 11.2477 0.562535 11.3084 0.537376C11.3692 0.512217 11.4343 0.499268 11.5 0.499268C11.5658 0.499268 11.6309 0.512217 11.6916 0.537376C11.7523 0.562535 11.8075 0.599411 11.854 0.645899C11.9005 0.692387 11.9374 0.747576 11.9625 0.808316C11.9877 0.869055 12.0007 0.934155 12.0007 0.999899C12.0007 1.06564 11.9877 1.13074 11.9625 1.19148C11.9374 1.25222 11.9005 1.30741 11.854 1.3539L6.70702 6.4999L11.854 11.6459C11.9005 11.6924 11.9374 11.7476 11.9625 11.8083C11.9877 11.8691 12.0007 11.9342 12.0007 11.9999C12.0007 12.0656 11.9877 12.1307 11.9625 12.1915C11.9374 12.2522 11.9005 12.3074 11.854 12.3539C11.8075 12.4004 11.7523 12.4373 11.6916 12.4624C11.6309 12.4876 11.5658 12.5005 11.5 12.5005C11.4343 12.5005 11.3692 12.4876 11.3084 12.4624C11.2477 12.4373 11.1925 12.4004 11.146 12.3539L6.00002 7.2069L0.854021 12.3539C0.807533 12.4004 0.752344 12.4373 0.691605 12.4624C0.630865 12.4876 0.565765 12.5005 0.500021 12.5005C0.434277 12.5005 0.369177 12.4876 0.308438 12.4624C0.247698 12.4373 0.192509 12.4004 0.146021 12.3539C0.0995332 12.3074 0.0626571 12.2522 0.037498 12.1915C0.0123389 12.1307 -0.000610352 12.0656 -0.000610352 11.9999C-0.000610352 11.9342 0.0123389 11.8691 0.037498 11.8083C0.0626571 11.7476 0.0995332 11.6924 0.146021 11.6459L5.29302 6.4999L0.146021 1.3539Z" fill="currentColor"></path>
            </svg>
          </a>
        </div>
      </div>

      <div class="col-md-4">
        <div class="select filter-wrap">
          <p class="" @click="isSortMenuOpen = !isSortMenuOpen">{{ activeSorting }}</p>
          <ul class="filter-body" :class="[isSortMenuOpen ? 'sorting-body' : 'hidden']">
            <li v-for="sorting in getSortingOrders" :key="sorting.key"
            @click="currentSortingOrder = sorting.key; isSortMenuOpen = false;"
                :class="{ 'active-sorting': sorting.key === currentSortingOrder }">
              {{ sorting.label }}
            </li>
          </ul>
        </div>
      </div>

    </div>
  </div>
</template>
